import { useReducer } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  FilterPool,
  Condition,
  Comparison,
  NbaRatesTeamPlayersResponse,
  NbaRatesStatsResponse,
  NbaRatesStatsWithBaselineResponse,
  NbaRatesAggregateStats,
  NbaRatesPlayerStats,
  OptionalFilters,
} from './types';

const PBPSTATS_BASE = 'https://api.pbpstats.com';

// ─── Filter Pool Reducer ─────────────────────────────────────────────────────

type FilterPoolAction =
  | { type: 'ADD_POOL' }
  | { type: 'REMOVE_POOL'; poolId: string }
  | { type: 'TOGGLE_PLAYER'; poolId: string; playerId: string }
  | { type: 'SET_CONDITION'; poolId: string; condition: Condition }
  | { type: 'SET_COMPARISON'; poolId: string; comparison: Comparison }
  | { type: 'SET_COUNT'; poolId: string; count: number }
  | { type: 'SELECT_ALL'; poolId: string; playerIds: string[] }
  | { type: 'CLEAR_POOL'; poolId: string }
  | { type: 'RESET' };

function filterPoolReducer(state: FilterPool[], action: FilterPoolAction): FilterPool[] {
  switch (action.type) {
    case 'ADD_POOL':
      return [
        ...state,
        {
          id: `pool-${Date.now()}`,
          playerIds: [],
          condition: 'OnFloor' as Condition,
          comparison: 'AtLeast' as Comparison,
          count: 1,
        },
      ];

    case 'REMOVE_POOL':
      return state.filter((p) => p.id !== action.poolId);

    case 'TOGGLE_PLAYER': {
      return state.map((pool) => {
        if (pool.id !== action.poolId) return pool;
        const isSelected = pool.playerIds.includes(action.playerId);
        const newPlayerIds = isSelected
          ? pool.playerIds.filter((id) => id !== action.playerId)
          : [...pool.playerIds, action.playerId];
        return {
          ...pool,
          playerIds: newPlayerIds,
          count: Math.min(pool.count, Math.max(1, newPlayerIds.length)),
        };
      });
    }

    case 'SET_CONDITION':
      return state.map((p) =>
        p.id === action.poolId ? { ...p, condition: action.condition } : p,
      );

    case 'SET_COMPARISON':
      return state.map((p) =>
        p.id === action.poolId ? { ...p, comparison: action.comparison } : p,
      );

    case 'SET_COUNT':
      return state.map((p) =>
        p.id === action.poolId ? { ...p, count: action.count } : p,
      );

    case 'SELECT_ALL':
      return state.map((p) => {
        if (p.id !== action.poolId) return p;
        return {
          ...p,
          playerIds: action.playerIds,
          count: Math.min(p.count, action.playerIds.length),
        };
      });

    case 'CLEAR_POOL':
      return state.map((p) =>
        p.id === action.poolId ? { ...p, playerIds: [], count: 1 } : p,
      );

    case 'RESET':
      return [];

    default:
      return state;
  }
}

export function useNbaRatesFilters() {
  const [filterPools, dispatch] = useReducer(filterPoolReducer, []);
  return { filterPools, dispatch };
}

// ─── PBPStats API ────────────────────────────────────────────────────────────

async function fetchPbpStats(params: Record<string, string>): Promise<any> {
  const qs = new URLSearchParams(params).toString();
  const response = await fetch(`${PBPSTATS_BASE}/get-wowy-stats/nba?${qs}`);
  if (!response.ok) throw new Error(`PBPStats request failed: ${response.status}`);
  return response.json();
}

// ─── Filter Conversion ──────────────────────────────────────────────────────

const COMPARISON_TO_PBPSTATS: Record<Comparison, string> = {
  AtLeast: 'GreaterThan',
  Exactly: 'Exactly',
  AtMost: 'LessThan',
};

/** Convert filter pools directly to PBPStats query params: {index}{Comparison}{count}{Condition}=player1,player2 */
function filtersToParams(pools: FilterPool[]): Record<string, string> {
  const params: Record<string, string> = {};
  pools
    .filter((pool) => pool.playerIds.length > 0)
    .forEach((pool, i) => {
      const key = `${i}${COMPARISON_TO_PBPSTATS[pool.comparison]}${pool.count}${pool.condition}`;
      params[key] = pool.playerIds.join(',');
    });
  return params;
}

// ─── PBPStats Response Mapping ───────────────────────────────────────────────

function num(row: any, key: string): number {
  const v = row?.[key];
  return typeof v === 'number' ? v : Number(v) || 0;
}

function pctOrNull(row: any, key: string): number | null {
  const v = row?.[key];
  if (v == null || v === 0) return null;
  return typeof v === 'number' ? v : Number(v) || null;
}

function mapTeamPlayers(data: any, teamId: string, seasons: string): NbaRatesTeamPlayersResponse {
  const rows: any[] = data?.multi_row_table_data ?? [];
  const seen = new Set<string>();
  const players = rows
    .filter((row) => {
      const id = String(row.EntityId ?? '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((row) => ({
      player_id: String(row.EntityId),
      player_name: String(row.Name ?? ''),
      team_id: teamId,
    }))
    .sort((a, b) => a.player_name.localeCompare(b.player_name));

  return { team_id: teamId, season: seasons, players };
}

function mapAggregate(data: any): NbaRatesAggregateStats {
  const row = data?.single_row_table_data;
  if (!row) {
    return {
      minutes: 0, possessions: 0, off_poss: 0, def_poss: 0,
      off_rtg: null, def_rtg: null, net_rtg: null,
      fg2_pct: null, fg3_pct: null, efg_pct: null, ts_pct: null, usage: null,
    };
  }

  const offPoss = num(row, 'OffPoss');
  const defPoss = num(row, 'DefPoss');
  const points = num(row, 'Points');
  const oppPoints = num(row, 'OpponentPoints');
  const offRtg = offPoss > 0 ? (points / offPoss) * 100 : null;
  const defRtg = defPoss > 0 ? (oppPoints / defPoss) * 100 : null;

  return {
    minutes: num(row, 'Minutes'),
    possessions: num(row, 'TotalPoss') || num(row, 'Possessions') || offPoss + defPoss,
    off_poss: offPoss,
    def_poss: defPoss,
    off_rtg: offRtg,
    def_rtg: defRtg,
    net_rtg: offRtg != null && defRtg != null ? offRtg - defRtg : null,
    fg2_pct: pctOrNull(row, 'Fg2Pct'),
    fg3_pct: pctOrNull(row, 'Fg3Pct'),
    efg_pct: pctOrNull(row, 'EfgPct'),
    ts_pct: pctOrNull(row, 'TsPct'),
    usage: pctOrNull(row, 'Usage'),
  };
}

function mapPlayers(data: any): NbaRatesPlayerStats[] {
  const rows: any[] = data?.multi_row_table_data ?? [];
  return rows.map((row) => {
    const fg2a = num(row, 'FG2A');
    const fg2m = num(row, 'FG2M');
    const fg3a = num(row, 'FG3A');
    const fg3m = num(row, 'FG3M');
    const fta = num(row, 'FTA');
    const ftm = num(row, 'FtPoints'); // PBPStats names FTM as "FtPoints"
    const fga = fg2a + fg3a;
    const fgm = fg2m + fg3m;
    const pts = num(row, 'Points');
    const ast = num(row, 'Assists');
    const tov = num(row, 'Turnovers');
    const oreb = num(row, 'OffRebounds');
    const dreb = num(row, 'DefRebounds');
    const offPoss = num(row, 'OffPoss');
    const defPoss = num(row, 'DefPoss');
    const oppPts = num(row, 'OpponentPoints');

    // DRtg uses OpponentPoints (team-level: opponent pts while this player is on court).
    // ORtg cannot be computed per-player: Points is individual scoring, not team points
    // on court, so (pts / offPoss * 100) would just be PTS per 100 possessions.
    const defRtg = defPoss > 0 ? (oppPts / defPoss) * 100 : null;
    const playDenom = fga + 0.44 * fta + ast + tov;
    const tovDenom = fga + 0.44 * fta + tov;

    // Shot zone stats
    const atRimFga = num(row, 'AtRimFGA');
    const atRimFgm = num(row, 'AtRimFGM');
    const shortMidFga = num(row, 'ShortMidRangeFGA');
    const shortMidFgm = num(row, 'ShortMidRangeFGM');
    const longMidFga = num(row, 'LongMidRangeFGA');
    const longMidFgm = num(row, 'LongMidRangeFGM');
    const corner3Fga = num(row, 'Corner3FGA');
    const corner3Fgm = num(row, 'Corner3FGM');
    const arc3Fga = num(row, 'Arc3FGA');
    const arc3Fgm = num(row, 'Arc3FGM');

    // Turnover details
    const badPassTov = num(row, 'BadPassTurnovers');
    const lostBallTov = num(row, 'LostBallTurnovers');

    return {
      player_id: String(row.EntityId ?? ''),
      player_name: String(row.Name ?? ''),
      minutes: num(row, 'Minutes'),
      possessions: num(row, 'TotalPoss') || num(row, 'Possessions') || offPoss + defPoss,
      off_poss: offPoss,
      def_poss: defPoss,
      pts,
      reb: oreb + dreb,
      ast,
      stl: num(row, 'Steals'),
      blk: num(row, 'Blocks'),
      tov,
      oreb,
      dreb,
      fg2a, fg2m, fg3a, fg3m, fta, ftm,
      fg2_pct: fg2a > 0 ? (fg2m / fg2a) * 100 : null,
      fg3_pct: fg3a > 0 ? (fg3m / fg3a) * 100 : null,
      ft_pct: fta > 0 ? (ftm / fta) * 100 : null,
      efg_pct: fga > 0 ? ((fg2m + fg3m + 0.5 * fg3m) / fga) * 100 : null,
      ts_pct: fga + 0.44 * fta > 0 ? (pts / (2 * (fga + 0.44 * fta))) * 100 : null,
      fg3a_pct: fga > 0 ? (fg3a / fga) * 100 : null,
      usage: pctOrNull(row, 'Usage'),
      off_rtg: null,
      def_rtg: defRtg,
      net_rtg: null,
      ast_ratio: playDenom > 0 ? (ast / playDenom) * 100 : null,
      tov_pct: tovDenom > 0 ? (tov / tovDenom) * 100 : null,
      off_reb_pct: pctOrNull(row, 'OffFGReboundPct'),
      def_reb_pct: pctOrNull(row, 'DefFGReboundPct'),
      // ─── Extended Fields ──────────────────────────────────────────────
      // Totals
      fga,
      fgm,
      fg_pct: fga > 0 ? (fgm / fga) * 100 : null,
      // General
      plus_minus: num(row, 'PlusMinus'),
      // Efficiency
      ppp: offPoss > 0 ? pts / offPoss : null,
      ftr: fga > 0 ? fta / fga : null,
      shot_quality_avg: pctOrNull(row, 'ShotQualityAvg'),
      // Shot location - At Rim
      at_rim_fga: atRimFga,
      at_rim_fgm: atRimFgm,
      at_rim_pct: pctOrNull(row, 'AtRimAccuracy'),
      at_rim_freq: pctOrNull(row, 'AtRimFrequency'),
      at_rim_ast_pct: pctOrNull(row, 'AtRimPctAssisted'),
      at_rim_blk_pct: pctOrNull(row, 'AtRimPctBlocked'),
      // Shot location - Short Mid Range
      short_mid_fga: shortMidFga,
      short_mid_fgm: shortMidFgm,
      short_mid_pct: pctOrNull(row, 'ShortMidRangeAccuracy'),
      short_mid_freq: pctOrNull(row, 'ShortMidRangeFrequency'),
      // Shot location - Long Mid Range
      long_mid_fga: longMidFga,
      long_mid_fgm: longMidFgm,
      long_mid_pct: pctOrNull(row, 'LongMidRangeAccuracy'),
      long_mid_freq: pctOrNull(row, 'LongMidRangeFrequency'),
      // Shot location - Corner 3
      corner3_fga: corner3Fga,
      corner3_fgm: corner3Fgm,
      corner3_pct: pctOrNull(row, 'Corner3Accuracy'),
      corner3_freq: pctOrNull(row, 'Corner3Frequency'),
      // Shot location - Arc (Above Break) 3
      arc3_fga: arc3Fga,
      arc3_fgm: arc3Fgm,
      arc3_pct: pctOrNull(row, 'Arc3Accuracy'),
      arc3_freq: pctOrNull(row, 'Arc3Frequency'),
      // Playmaking (extended)
      assist_points: num(row, 'AssistPoints'),
      two_pt_assists: num(row, 'TwoPtAssists'),
      three_pt_assists: num(row, 'ThreePtAssists'),
      assisted_2s_pct: pctOrNull(row, 'Assisted2sPct'),
      assisted_3s_pct: pctOrNull(row, 'Assisted3sPct'),
      ast_tov_ratio: tov > 0 ? ast / tov : null,
      // Fouls
      fouls: num(row, 'Fouls'),
      fouls_drawn: num(row, 'FoulsDrawn'),
      shooting_fouls_drawn_pct: pctOrNull(row, 'ShootingFoulsDrawnPct'),
      and1_trips: num(row, '2pt And 1 Free Throw Trips'),
      loose_ball_fouls_drawn: num(row, 'Loose Ball Fouls Drawn'),
      offensive_fouls: num(row, 'Offensive Fouls'),
      // Defense (extended)
      blocks_recovered_pct: pctOrNull(row, 'BlocksRecoveredPct'),
      recovered_blocks: num(row, 'RecoveredBlocks'),
      // Turnovers (detailed)
      live_ball_tov_pct: pctOrNull(row, 'LiveBallTurnoverPct'),
      bad_pass_tov: badPassTov,
      lost_ball_tov: lostBallTov,
      // Second Chance
      second_chance_pts: num(row, 'SecondChancePoints'),
      // Rebounding (extended)
      self_oreb_pct: pctOrNull(row, 'SelfORebPct'),
    };
  });
}

// ─── React Query Hooks ───────────────────────────────────────────────────────

/** Fetch team players for filter selection */
export function useTeamPlayers(teamId: string, seasons: string) {
  return useQuery<NbaRatesTeamPlayersResponse>({
    queryKey: ['nba-rates-team-players', teamId, seasons],
    queryFn: async () => {
      const data = await fetchPbpStats({
        Season: seasons,
        SeasonType: 'Regular Season',
        TeamId: teamId,
        Type: 'Player',
      });
      return mapTeamPlayers(data, teamId, seasons);
    },
    enabled: !!teamId && !!seasons,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Fetch NBA Rates stats. Manual trigger only (enabled: false).
 * Makes 2 PBPStats calls per dataset (aggregate + player breakdown).
 * When includeBaseline is true, makes 2 more calls without player filters.
 */
export function useNbaRatesStats(params: {
  teamId: string;
  seasons: string;
  seasonType: string;
  filterPools: FilterPool[];
  optionalFilters: OptionalFilters;
  includeBaseline: boolean;
}) {
  const { teamId, seasons, seasonType, filterPools, optionalFilters, includeBaseline } = params;
  const filterParams = filtersToParams(filterPools);

  return useQuery<NbaRatesStatsWithBaselineResponse>({
    queryKey: [
      'nba-rates-stats',
      teamId,
      seasons,
      seasonType,
      JSON.stringify(filterParams),
      JSON.stringify(optionalFilters),
      includeBaseline,
    ],
    queryFn: async () => {
      // Common PBPStats params
      const baseParams: Record<string, string> = {
        Season: seasons,
        SeasonType: seasonType,
        TeamId: teamId,
      };

      if (optionalFilters.leverage.length > 0)
        baseParams.Leverage = optionalFilters.leverage.join(',');
      if (optionalFilters.periods.length > 0)
        baseParams.Period = optionalFilters.periods.join(',');
      if (optionalFilters.opponent)
        baseParams.OpponentTeamId = optionalFilters.opponent;
      if (optionalFilters.fromMargin != null)
        baseParams.FromMargin = String(optionalFilters.fromMargin);
      if (optionalFilters.toMargin != null)
        baseParams.ToMargin = String(optionalFilters.toMargin);
      if (optionalFilters.fromDate)
        baseParams.FromDate = optionalFilters.fromDate;
      if (optionalFilters.toDate)
        baseParams.ToDate = optionalFilters.toDate;
      if (optionalFilters.homeAway)
        baseParams.Location = optionalFilters.homeAway === 'home' ? 'Home' : 'Away';

      const filteredParams = { ...baseParams, ...filterParams };

      // Fetch filtered: aggregate (no Type) + player breakdown (Type=Player)
      const [filteredAgg, filteredPlayers] = await Promise.all([
        fetchPbpStats(filteredParams),
        fetchPbpStats({ ...filteredParams, Type: 'Player' }),
      ]);

      const filtered: NbaRatesStatsResponse = {
        aggregate: mapAggregate(filteredAgg),
        players: mapPlayers(filteredPlayers),
        query_params: filteredParams,
        fetched_at: new Date().toISOString(),
      };

      // Baseline: same query without player filters
      let baseline: NbaRatesStatsResponse;
      if (includeBaseline) {
        const [baselineAgg, baselinePlayers] = await Promise.all([
          fetchPbpStats(baseParams),
          fetchPbpStats({ ...baseParams, Type: 'Player' }),
        ]);
        baseline = {
          aggregate: mapAggregate(baselineAgg),
          players: mapPlayers(baselinePlayers),
          query_params: baseParams,
          fetched_at: new Date().toISOString(),
        };
      } else {
        baseline = filtered;
      }

      return { filtered, baseline };
    },
    enabled: false,
    staleTime: 5 * 60 * 1000,
  });
}
